import { get, isEmpty, set } from 'lodash';

export default {
    namespaced: true,

    state: {
        disambiguate: false,
        dynamic: {},
        form: null,
        heroBullets: [],
        preHeadline: '',
        // The firm a visitor chose from the Attorney directory, as the card supplied it.
        // Held in memory for the life of the page and read through the guarded getter.
        preferredTenant: null,
        location: {
            id: null,
            region_id: null,
            criteria_id: null,
            name: null,
            City: null,
            State: null,
            ZipCode: null
        },
        logoSrc: '',
        regions: [],
        selectedRegion: {},
        redirectUrl: '',
        pageLocation: '',
        mode: 'lead-capture'
    },

    getters: {
        /**
         * @param {object} state
         *
         * @returns {string|null}
         */
        city(state) {
            return state.location.City;
        },

        /**
         * @param {object} state
         *
         * @returns {string|null}
         */
        selectedLocation(state) {
            return state.location;
        },

        /**
         * @param {object} state
         *
         * @returns {string|null}
         */
        countyName(state) {
            if (! isEmpty(state.regions)) {
                return `${get(state.selectedRegion, 'County')}, ${get(state.selectedRegion, 'State')}`
            }
        },

        /**
         * @param {object} state
         *
         * @returns {boolean}
         */
        disambiguate(state) {
            return state.disambiguate;
        },

        /**
         * @param {object} state
         *
         * @returns {object}
         */
        dynamic(state) {
            return state.dynamic;
        },

        /**
         * @param {object} state
         *
         * @returns {object|null}
         */
        form(state) {
            return state.form;
        },

        /**
         * @param {object} state
         *
         * @returns {array}
         */
        heroBullets(state) {
            return state.heroBullets;
        },

        /**
         * @param {object} state
         *
         * @returns {string}
         */
        preHeadline(state) {
            return state.preHeadline;
        },

        /**
         * The chosen firm in the shape the intake plugin's `preferred-tenant` prop
         * requires, or null when there is nothing complete enough to send.
         *
         * The guard lives here rather than at the call site so that every consumer is
         * protected by construction. A listing carrying no Id — which is every listing
         * until ua-app's directory endpoint exposes one — yields null, the plugin sends
         * no preferred_tenant_id, and the intake behaves as it did before the preflight.
         *
         * @param {object} state
         *
         * @returns {{id: number, name: string}|null}
         */
        preferredTenant(state) {
            const id = Number(get(state.preferredTenant, 'id'));
            const name = get(state.preferredTenant, 'name');

            if (! Number.isInteger(id) || id <= 0 || typeof name !== 'string' || name.trim() === '') {
                return null;
            }

            return { id, name: name.trim() };
        },

        /**
         * @param {object} state
         *
         * @returns {number|null}
         */
        locationId(state) {
            return state.location.id;
        },

        /**
         * @param {object} state
         *
         * @returns {number|null}
         */
        regionId(state) {
            return state.location.region_id;
        },

        /**
         * @param {object} state
         *
         * @returns {number|null}
         */
        criteriaId(state) {
            return state.location.criteria_id;
        },

        /**
         * @param {object} state
         *
         * @returns {string}
         */
        logoSrc(state) {
            return state.logoSrc;
        },

        /**
         * @param {object} state
         *
         * @returns {array}
         */
        regions(state) {
            return state.regions;
        },

        /**
         * @param {object} state
         *
         * @returns {string}
         */
        redirectUrl(state) {
            return state.redirectUrl;
        },

        /**
         * @param {object} state
         *
         * @returns {string}
         */
        pageLocation(state) {
            return state.pageLocation;
        },

        /**
         * @param {object} state
         *
         * @returns {object}
         */
        selectedRegion(state) {
            return state.selectedRegion;
        },

        /**
         * @param {object} state
         *
         * @returns {string|null}
         */
        state(state) {
            return state.location.State;
        },

        /**
         * @param {object} state
         *
         * @returns {boolean}
         */
        stepLocationIsIncomplete(state) {
            return state.location.City === null
                || state.location.id === null
                || state.location.State === null;
        },

        /**
         * @param {object} state
         *
         * @returns {boolean}
         */
        stepIssueIsIncomplete(state) {
            return state.form.issue.length < 10
                || state.form.issue.length > 650
                || state.form.issue.split(' ').length < 2;
        },

        /**
         * @param {object} state
         *
         * @returns {string|null}
         */
        zipCode(state) {
            return state.location.ZipCode;
        },

        /**
         * @param {object} state
         *
         * @returns {string|null}
         */
        mode(state) {
            return state.mode;
        }
    },

    mutations: {
        /**
         * @param {object} state
         * @param {string} city
         */
        city(state, city) {
            set(state.location, 'City', city);
            set(state.form, 'city', city);
        },

        /**
         * @param {object} state
         * @param {boolean} disambiguate
         */
        disambiguate(state, disambiguate) {
            state.disambiguate = disambiguate;
        },

        /**
         * @param {object} state
         * @param {object} dynamic
         */
        dynamic(state, dynamic) {
            state.dynamic = dynamic;
        },

        /**
         * @param {object} state
         * @param {array} heroBullets
         */
        heroBullets(state, heroBullets) {
            state.heroBullets = heroBullets;
        },

        /**
         * @param {object} state
         * @param {string} preHeadline
         */
        preHeadline(state, preHeadline) {
            state.preHeadline = preHeadline;
        },

        /**
         * Stored as supplied and validated by the getter, so that choosing a second
         * firm simply replaces the first and an incomplete listing cannot half-set it.
         *
         * @param {object} state
         * @param {object|null} preferredTenant
         */
        preferredTenant(state, preferredTenant) {
            state.preferredTenant = preferredTenant;
        },

        /**
         * @param {object} state
         * @param {Form} form
         */
        initializeForm(state, form) {
            state.form = form;
        },

        /**
         * @param {object} state
         * @param {string} location
         */
        location(state, location) {
            state.location = location;
            set(state.form, 'location', location);
        },

        /**
         * @param {object} state
         * @param {number} locationId
         */
        locationId(state, locationId) {
            set(state.location, 'id', locationId);
        },

        /**
         * @param {object} state
         * @param {number} criteria_id
         */
        criteria_id(state, criteria_id) {
            set(state.location, 'criteria_id', criteria_id);
        },

        /**
         * @param {object} state
         * @param {number} region_id
         */
        region_id(state, region_id) {
            set(state.location, 'region_id', region_id);
        },

        /**
         * @param {object} state
         * @param {string} logoSrc
         */
        logoSrc(state, logoSrc) {
            state.logoSrc = logoSrc;
        },

        /**
         * @param {object} state
         * @param {array} regions
         */
        regions(state, regions) {
            state.regions = regions;
        },

        /**
         * @param {object} state
         * @param {string} redirectUrl
         */
        redirectUrl(state, redirectUrl) {
            state.redirectUrl = redirectUrl;
        },

        /**
         * @param {object} state
         * @param {string} pageLocation
         */
        pageLocation(state, pageLocation) {
            state.pageLocation = pageLocation;
        },

        /**
         * @param {object} state
         * @param {object} selectedRegion
         */
        selectedRegion(state, selectedRegion) {
            state.selectedRegion = selectedRegion;
        },

        /**
         * @param {object} state
         * @param {string} stateName
         */
        state(state, stateName) {
            set(state.location, 'State', stateName);
        },

        /**
         * @param {object} state
         * @param {string} mode
         */
        mode(state, mode) {
            state.mode = mode;
        },

        /**
         * @param {object} state
         * @param {object} formData
         */
        updateForm(state, formData) {
            state.form = { ...state.form, ...formData}
        }
    }
};
